import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Activity } from 'lucide-react';

interface LiveSession {
  session_id: string;
  current_step: number;
  device_type: string | null;
  is_completed: boolean;
  updated_at: string;
}

const SCREEN_NAMES: Record<number, string> = {
  1: 'Intro + Leeftijd',
  2: 'Verbeteren',
  3: 'Rimpels',
  4: 'Waar werken',
  5: 'Social proof',
  6: 'Zon',
  7: 'Slaap',
  8: 'Stress',
  9: 'Huidtint',
  10: 'Gevoeligheid',
  11: 'Match + Loader',
  12: 'Huidprofiel',
  13: 'Eerdere oplossingen',
  14: 'Info',
  15: 'Tijd',
  16: 'Info 2',
  17: 'Gevoel',
  18: 'Stralende versie',
  19: 'Gelegenheid',
  20: 'Resultaten klaar',
  21: 'Resultaten',
};

function timeAgo(dateStr: string): string {
  const diff = Math.round((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 5) return 'nu';
  if (diff < 60) return `${diff}s geleden`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m geleden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}u geleden`;
  return `${Math.floor(diff / 86400)}d geleden`;
}

const LiveActivityFeed = () => {
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [, setTick] = useState(0);

  useEffect(() => {
    // Fetch recent active sessions (last 30 min)
    const fetchSessions = async () => {
      const cutoff = new Date(Date.now() - 30 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from('quiz_sessions')
        .select('session_id, current_step, device_type, is_completed, updated_at')
        .not('utm_source', 'is', null)
        .gte('updated_at', cutoff)
        .order('updated_at', { ascending: false })
        .limit(20);
      if (data) setSessions(data);
    };

    fetchSessions();

    // Subscribe to realtime changes
    const channel = supabase
      .channel('live-quiz-sessions')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'quiz_sessions' },
        (payload) => {
          const newRow = (payload.new as LiveSession);
          if (!newRow?.session_id) return;
          setSessions(prev => {
            const filtered = prev.filter(s => s.session_id !== newRow.session_id);
            return [newRow, ...filtered].slice(0, 20);
          });
        }
      )
      .subscribe();

    // Update "time ago" every 5 seconds
    const ticker = setInterval(() => setTick(t => t + 1), 5000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(ticker);
    };
  }, []);

  const activeSessions = sessions.filter(s => !s.is_completed);
  const recentCompleted = sessions.filter(s => s.is_completed);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
        <Activity className="w-4 h-4" />
        Live activiteit
        {activeSessions.length > 0 && (
          <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-emerald-600">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            {activeSessions.length} actief
          </span>
        )}
      </h3>

      {sessions.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">Geen recente activiteit</p>
      ) : (
        <div className="space-y-2 max-h-[350px] overflow-y-auto">
          {sessions.map(s => {
            const step = Math.max(s.current_step, 1);
            const screenName = SCREEN_NAMES[step] || `Scherm ${step}`;
            const shortId = s.session_id.slice(-5);
            const device = s.device_type === 'mobile' ? '📱' : s.device_type === 'tablet' ? '📱' : '💻';

            return (
              <div
                key={s.session_id}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                  s.is_completed
                    ? 'bg-emerald-50 border border-emerald-100'
                    : 'bg-gray-50 border border-gray-100'
                }`}
              >
                <span className="text-base">{device}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-400">#{shortId}</span>
                    {s.is_completed ? (
                      <span className="text-emerald-600 font-medium">✅ Voltooid</span>
                    ) : (
                      <span className="text-gray-700">
                        Scherm {step}
                        <span className="text-gray-400 ml-1">({screenName})</span>
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap">{timeAgo(s.updated_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LiveActivityFeed;
